import { withTimeoutSignal } from './abort-signal-helpers';
import { del, get, patch, post, put } from './methods';
import { isServer, withRetry } from './retry';
import type { ApiError, CreateRequest, RequestMethod, RetryOptions } from './types';

const DEFAULT_TIMEOUT_MS = 5_000;
const REQUEST_TIMEOUT_STATUS = 504;
const REQUEST_TIMEOUT_ERROR_CODE = 'REQUEST_TIMEOUT';

/**
 * Create an API method. This abstraction is useful to create more specific
 * API function calls with declared input/output types.
 * @example
 * const getUser = createAPIMethod<{ id: string }, { name: string }>({
 *   method: 'GET',
 *   url: '/user',
 * });
 *
 * // This has fully typed parameters and response.
 * const user = await getUser({ id: '1' });
 */
export const createRequest: CreateRequest = (opts) => async (input) => {
  let method;

  switch (opts.method) {
    case 'GET':
      method = get;
      break;
    case 'POST':
      method = post;
      break;
    case 'DELETE':
      method = del;
      break;
    case 'PUT':
      method = put;
      break;
    case 'PATCH':
      method = patch;
      break;
    default:
      method = get;
  }

  const curl = requestToCurl(
    opts.url,
    opts.method ?? 'GET',
    input,
    opts.fetchOptions?.headers as Record<string, string>,
  );

  const debugInfo = opts.debug ? { curl } : null;
  const timeoutMs = opts.fetchOptions?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const retryConfig = resolveRetryConfig(opts.fetchOptions?.retry, opts.method ?? 'GET');
  const baseFetchOptions = opts.fetchOptions ?? {};
  let response: Response;

  try {
    response = await withRetry(async () => {
      const { fetchOptions, cleanup } = withTimeoutSignal(
        baseFetchOptions,
        timeoutMs,
        Boolean(opts.debug),
      );
      try {
        return await method(opts.url, input ?? {}, fetchOptions);
      } finally {
        cleanup();
      }
    }, retryConfig);
  } catch (error) {
    if (!isTimeoutError(error)) {
      throw error;
    }

    const timeoutResponse = timeoutErrorResponse(timeoutMs);

    if (debugInfo) {
      debugLog({
        curl,
        url: opts.url,
        status: timeoutResponse.status,
        ok: timeoutResponse.ok,
        data: timeoutResponse.data,
        isText: false,
      });
    }

    // Cast required: CreateRequest's ErrorOutput generic can't know about the
    // timeout shape at the type level, but at runtime this always returns ApiError.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return timeoutResponse as any;
  }

  const status = response.status;

  // Clone the response to avoid stream lock if json fails.
  const clonedResponse = response.clone();

  try {
    const data = await response.json();
    if (debugInfo) {
      debugLog({ curl, url: opts.url, status, ok: response.ok, data, isText: false });
    }
    return { data, status, headers: response.headers, ok: response.ok };
  } catch (_) {
    const data = await clonedResponse.text();
    if (debugInfo) {
      debugLog({ curl, url: opts.url, status, ok: clonedResponse.ok, data, isText: true });
    }
    return { data, status, headers: clonedResponse.headers, ok: clonedResponse.ok };
  }
};

const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'DELETE']);

function resolveRetryConfig(
  retry: RetryOptions | false | undefined,
  method: RequestMethod,
): RetryOptions {
  if (retry === false) return { maxRetries: 0 };
  if (retry) return retry;
  // Default: retry only idempotent methods on the server.
  // POST/PUT/PATCH require explicit opt-in to avoid replaying writes.
  if (isServer() && IDEMPOTENT_METHODS.has(method)) return {};
  return { maxRetries: 0 };
}

function isTimeoutError(error: unknown): boolean {
  let current: unknown = error;
  let depth = 0;

  while (typeof current === 'object' && current !== null && depth < 5) {
    if ('name' in current && (current as { name: unknown }).name === 'TimeoutError') {
      return true;
    }

    if (
      'name' in current &&
      (current as { name: unknown }).name === 'AggregateError' &&
      'errors' in current &&
      Array.isArray((current as { errors: unknown }).errors)
    ) {
      const hasEtimedout = (current as { errors: unknown[] }).errors.some(
        (e: unknown) =>
          (typeof e === 'object' &&
            e !== null &&
            'code' in e &&
            (e as { code: unknown }).code === 'ETIMEDOUT') ||
          (e instanceof Error && e.message.includes('ETIMEDOUT')),
      );
      if (hasEtimedout) return true;
    }

    current = 'cause' in current ? (current as { cause: unknown }).cause : null;
    depth++;
  }

  return false;
}

function timeoutErrorResponse(timeoutMs: number): {
  data: ApiError;
  status: number;
  headers: Headers;
  ok: false;
} {
  return {
    data: {
      errorCode: REQUEST_TIMEOUT_ERROR_CODE,
      message: `Request timed out after ${timeoutMs}ms.`,
    },
    status: REQUEST_TIMEOUT_STATUS,
    headers: new Headers(),
    ok: false,
  };
}

function requestToCurl(
  url: string,
  method: string,
  body?: unknown,
  headers?: Record<string, string>,
): string {
  let curl = [`curl -X ${method}`];

  if (headers) {
    for (const [key, value] of Object.entries(headers)) {
      curl.push(`-H '${key}: ${value}'`);
    }
  }

  if (body && method !== 'GET') {
    curl.push(`-d '${JSON.stringify(body)}'`);
  }

  curl.push(`'${url}'`);

  return curl.join(' ');
}

function debugLog(args: {
  curl: string;
  url: string;
  status: number;
  ok: boolean;
  data: unknown;
  isText: boolean;
}) {
  const { curl, url, status, ok, data, isText } = args;
  const write = (msg: string) => process.stdout.write(`${msg}\n`);
  write('\x1b[36m======================== DEBUG ========================\x1b[0m');
  write('\x1b[33mRequest (cURL):\x1b[0m');
  write(`\x1b[33m${curl}\x1b[0m`);
  write('\x1b[32mResponse:\x1b[0m');
  write(`\x1b[32m  URL: ${url}\x1b[0m`);
  write(`\x1b[32m  Status: ${status}\x1b[0m`);
  write(`\x1b[32m  OK: ${ok}\x1b[0m`);
  const pretty = isText || typeof data === 'string' ? String(data) : JSON.stringify(data, null, 2);
  write('\x1b[35m  Body:\x1b[0m');
  write(`\x1b[35m${pretty}\x1b[0m`);
  write('\x1b[36m=======================================================\x1b[0m');
}
